import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../services/api";
import {
  clearStoredCoachSession,
  getStoredCoachSession,
  setStoredCoachSession,
  subscribeCoachSession,
} from "../services/auth";
import {
  hasCoachFeature,
  normalizeCoachPermissions,
} from "../constants/coachAccess";
import {
  getSubscriptionCapabilities,
  hasSubscriptionCapability,
  normalizeSubscriptionPlan,
} from "../constants/subscriptionAccess";

const CoachAuthContext = createContext(null);

export function CoachAuthProvider({ children }) {
  const [session, setSession] = useState(() => getStoredCoachSession());

  useEffect(() => subscribeCoachSession(setSession), []);

  useEffect(() => {
    if (!session?.token) return;

    let cancelled = false;
    apiFetch("/auth/me", { cacheTtlMs: 0 })
      .then((payload) => {
        if (cancelled || !payload?.user) return;
        setStoredCoachSession({
          token: session.token,
          user: payload.user,
        });
      })
      .catch(() => {
        if (!cancelled) {
          clearStoredCoachSession();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session?.token]);

  const login = useCallback(async ({ email, password }) => {
    const payload = await apiFetch("/auth/login", {
      method: "POST",
      body: { email, password },
      cacheTtlMs: 0,
    });

    if (payload?.user?.role !== "coach") {
      throw new Error("Ce compte n'a pas accès au front coach.");
    }

    const nextSession = {
      token: payload.token,
      user: payload.user,
    };
    setStoredCoachSession(nextSession);
    return nextSession;
  }, []);

  const activateInvite = useCallback(async ({ token, password }) => {
    const payload = await apiFetch("/auth/signup-from-invite", {
      method: "POST",
      body: { token, password },
      cacheTtlMs: 0,
    });

    if (payload?.user?.role !== "coach") {
      throw new Error("Cette invitation n'est pas destinée à l'espace coach.");
    }

    const nextSession = {
      token: payload.token,
      user: payload.user,
    };
    setStoredCoachSession(nextSession);
    return nextSession;
  }, []);

  const requestPasswordReset = useCallback(async ({ email }) => {
    return apiFetch("/auth/forgot-password", {
      method: "POST",
      body: { email },
      cacheTtlMs: 0,
    });
  }, []);

  const resetPassword = useCallback(async ({ token, password }) => {
    return apiFetch("/auth/reset-password", {
      method: "POST",
      body: { token, password },
      cacheTtlMs: 0,
    });
  }, []);

  const logout = useCallback(() => {
    clearStoredCoachSession();
  }, []);

  const value = useMemo(
    () => ({
      session,
      coach: session?.user || null,
      token: session?.token || "",
      isAuthenticated: Boolean(session?.token && session?.user?.id),
      isAdmin: Boolean(session?.user?.is_admin),
      permissions: normalizeCoachPermissions(
        session?.user?.coach_permissions,
        Boolean(session?.user?.is_admin)
      ),
      hasFeature: (feature) => hasCoachFeature(session?.user, feature),
      subscriptionPlan: normalizeSubscriptionPlan(session?.user?.subscription_plan),
      subscriptionCapabilities: getSubscriptionCapabilities(session?.user),
      hasSubscriptionCapability: (capability) =>
        hasSubscriptionCapability(session?.user, capability),
      login,
      activateInvite,
      requestPasswordReset,
      resetPassword,
      logout,
    }),
    [session, login, activateInvite, requestPasswordReset, resetPassword, logout]
  );

  return (
    <CoachAuthContext.Provider value={value}>
      {children}
    </CoachAuthContext.Provider>
  );
}

export function useCoachAuth() {
  const context = useContext(CoachAuthContext);
  if (!context) {
    throw new Error("useCoachAuth must be used within CoachAuthProvider");
  }
  return context;
}
